import React, { useRef } from 'react';
import { useQuery } from 'react-query';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Skeleton from '@material-ui/lab/Skeleton';
import _get from 'lodash/get';
import useSiteCopySelector from 'components/useSiteCopySelector';
import ModalDialog from 'components/ModalDialog';
import contentfulClient from 'utils/contentfulService';
import CopyButton from 'components/CopyButton';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import { useSelector } from 'react-redux';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import { formatHTMLToCopy } from './utils';

const useStyles = makeStyles(theme => ({
  modalContent: {
    maxHeight: '70vh',
    overflowY: 'auto',
    paddingRight: 24,
    paddingBottom: 12,
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
  },
  closeIcon: {
    top: 16,
    right: 16,
    padding: 4,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 18,
    marginTop: 4,
    marginBottom: 24,
  },
  headerTitle: {
    maxWidth: '80%',
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  closeButton: {
    position: 'absolute',
    right: 16,
    top: 16,
    color: '#666',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 8,
    marginTop: 8,
  },
  copyIconButton: {
    padding: 4,
    marginLeft: 8,
    color: theme.palette.primary.main,
    marginTop: -8,
  },
  locationItem: {
    fontSize: 14,
    lineHeight: '24px',
    margin: '4px 0',
    color: theme.palette.primary.main,
  },
  languageList: {
    listStyleType: 'disc',
    paddingLeft: 20,
    margin: 0,
    marginBottom: 8,
  },
  languageItem: {
    margin: '4px 0',
    fontSize: 14,
    lineHeight: '18px',
  },
  contactChip: {
    backgroundColor: '#f5f5f5',
    margin: '8px 0',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
  },
  contactLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontSize: 16,
  },
  copyAllButton: {
    whiteSpace: 'nowrap',
    flex: 'unset',
    marginRight: 12,
  },
  contactChipRoot: {
    borderRadius: 10,
    backgroundColor: '#EAF8FF',
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    margin: '16px 0',
    width: 'fit-content',
    gap: 8,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  contactName: {
    fontSize: 16,
    color: theme.palette.text.primary,
  },
  dialogContentRoot: {
    padding: 0,
  },
  copyTextClassName: {
    marginTop: -8,
  },
  dialogPaper: {
    paddingRight: 0,
  },
  copySuccessMessage: {
    marginLeft: 8,
    color: theme.palette.success.main,
    marginTop: -8,
  },
  richText: {
    '& p': {
      fontSize: '14px',
      lineHeight: '18px',
      marginTop: 6,
      marginBottom: 6,
    },
  },
  separator: {
    color: theme.palette.text.primary,
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
}));

// Fetch all available languages from Contentful
const getAvailableLanguages = async (_, ids) => {
  const languages = await contentfulClient.getEntries({
    content_type: 'language',
    'sys.id[in]': ids.join(','),
  });

  return languages;
};

const SectionHeader = ({
  title,
  trackEvent,
  getContentToCopy,
  getHTMLContentToCopy,
  classes,
}) => {
  const displayCopyButton = !!getContentToCopy;

  const handleTrackCopied = () => {
    if (trackEvent) {
      trackEvent('Client Resource - View More - Copied', { section: title });
    }
  };

  return (
    <Box className={classes.sectionHeader}>
      <Typography
        variant="h6"
        style={{
          fontSize: 14,
          lineHeight: '18px',
          fontWeight: 'bold',
          margin: '0px',
        }}
      >
        {title}
      </Typography>
      {displayCopyButton && (
        <CopyButton
          getContentToCopy={getContentToCopy}
          getHTMLContentToCopy={getHTMLContentToCopy}
          onCopied={handleTrackCopied}
          iconButton
          className={classes.copyIconButton}
          copyTextClassName={classes.copySuccessMessage}
          size="small"
        />
      )}
    </Box>
  );
};

const SectionContent = ({ children }) => {
  const theme = useTheme();

  return (
    <Box
      style={{
        fontSize: 14,
        lineHeight: '20px',
        color: theme.palette.text.primary,
      }}
    >
      {children}
      <div className="content-break" style={{ height: 1, marginTop: 5 }}></div>
    </Box>
  );
};

const ContextInfoPopup = ({ open, title, onClose, trackEvent, data = {} }) => {
  const classes = useStyles();
  const contentRef = useRef(null);
  const clientDetails = useSelector(makeSelectClientDetails());

  const internalContactTitle = _get(
    clientDetails,
    'metadata.internalContactTitle',
    'Internal Contact',
  );

  const showLanguagesInEnglish = _get(
    clientDetails,
    'metadata.languageLabelEn',
  );

  const {
    availableLanguages,
    languageNotes,
    section1Title,
    section2Title,
    section3Title,
    section4Title,
    section5Title,
    internalContact,
    internalContactEmail,
    internalContactPhone,
  } = data;

  const availableLanguagesIds = availableLanguages
    ?.map(language => _get(language, 'sys.id'))
    .filter(Boolean);

  const {
    data: availableLanguagesData,
    isFetching: isFetchingAvailableLanguages,
  } = useQuery(
    availableLanguagesIds?.length > 0 && [
      'availableLanguages',
      availableLanguagesIds,
    ],
    getAvailableLanguages,
    {
      staleTime: 10 * 60 * 1000, // 10 minutes
    },
  );

  const [resourceItemPageSiteCopy] = useSiteCopySelector([
    'resource-item-page',
  ]);

  const availableLanguagesText = _get(
    resourceItemPageSiteCopy,
    'pageCopy.availableLanguages',
  );

  const shouldShowInternalContact =
    internalContact || internalContactEmail || internalContactPhone;

  const sections = [
    {
      key: 'section1',
      title: section1Title,
    },
    {
      key: 'section2',
      title: section2Title,
    },
    {
      key: 'section3',
      title: section3Title,
    },
    {
      key: 'section4',
      title: section4Title,
    },
    {
      key: 'section5',
      title: section5Title,
    },
  ];

  const shouldShowAvailableLanguages =
    !_isEmpty(availableLanguagesIds) || !_isEmpty(languageNotes);

  const handleCopiedAll = () => {
    trackEvent('Client Resource - View More - Copied', { section: 'all' });
  };

  return (
    <ModalDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      noPadding
      dialogClasses={{
        paper: classes.dialogPaper,
        closeIcon: classes.closeIcon,
      }}
    >
      <Box className={classes.modalContent}>
        <Box className={classes.header}>
          <Typography className={classes.headerTitle} variant="h2">
            {title}{' '}
          </Typography>
          <CopyButton
            getContentToCopy={() =>
              `${title}\n\n${contentRef.current?.innerText}`
            }
            getHTMLContentToCopy={() =>
              formatHTMLToCopy(
                `<h2>${title}</h2>${contentRef.current?.innerHTML}`,
              )
            }
            buttonText={_get(
              resourceItemPageSiteCopy,
              'pageCopy.copyAllInfo',
              'Copy All Info',
            )}
            onCopied={handleCopiedAll}
            className={classes.copyAllButton}
          />
        </Box>
        <Box ref={contentRef}>
          {sections.map(
            section =>
              data?.[section.key] &&
              section.title && (
                <Box key={section.key} id={`section-${section.key}`}>
                  <SectionHeader
                    title={section.title}
                    trackEvent={trackEvent}
                    getContentToCopy={() =>
                      document.getElementById(`section-${section.key}`)
                        ?.innerText || ''
                    }
                    getHTMLContentToCopy={() =>
                      formatHTMLToCopy(
                        document.getElementById(`section-${section.key}`)
                          ?.innerHTML,
                      )
                    }
                    classes={classes}
                  />
                  <SectionContent>
                    <RichTextReactRenderer
                      data={data[section.key]}
                      className={classes.richText}
                      forceCMLinksOpenInNewTab
                    />
                  </SectionContent>
                </Box>
              ),
          )}

          {shouldShowAvailableLanguages && (
            <Box id="section-availableLanguages">
              <SectionHeader
                trackEvent={trackEvent}
                title={availableLanguagesText}
                getContentToCopy={() =>
                  document.getElementById('section-availableLanguages')
                    ?.innerText || ''
                }
                getHTMLContentToCopy={() =>
                  formatHTMLToCopy(
                    document.getElementById('section-availableLanguages')
                      ?.innerHTML,
                  )
                }
                classes={classes}
              />
              <SectionContent>
                {isFetchingAvailableLanguages ? (
                  <Skeleton variant="text" height={40} />
                ) : (
                  _get(availableLanguagesData, 'items', []).length > 0 && (
                    <ul className={classes.languageList}>
                      {_get(availableLanguagesData, 'items').map(
                        (language, index) => (
                          <li
                            key={language.sys.id || index}
                            className={classes.languageItem}
                          >
                            {showLanguagesInEnglish
                              ? _get(
                                  language,
                                  'fields.titleEnglish',
                                  _get(language, 'fields.title', ''),
                                )
                              : _get(language, 'fields.title', '')}
                          </li>
                        ),
                      )}
                    </ul>
                  )
                )}
                {languageNotes && (
                  <RichTextReactRenderer
                    data={languageNotes}
                    className={classes.richText}
                    forceCMLinksOpenInNewTab
                  />
                )}
              </SectionContent>
            </Box>
          )}
        </Box>
        {shouldShowInternalContact && (
          <Box id="section-internalContact">
            <SectionHeader title={internalContactTitle} classes={classes} />
            <SectionContent>
              <Box className={classes.contactChipRoot}>
                {internalContact && (
                  <Typography className={classes.contactName}>
                    {internalContact}
                  </Typography>
                )}
                {internalContactEmail && (
                  <a
                    href={`mailto:${internalContactEmail}`}
                    className={classes.contactLink}
                  >
                    {internalContactEmail}
                  </a>
                )}
                {internalContactPhone && (
                  <>
                    {internalContactEmail && (
                      <span className={classes.separator}>|</span>
                    )}
                    <a
                      href={`tel:${internalContactPhone}`}
                      className={classes.contactLink}
                    >
                      {internalContactPhone}
                    </a>
                  </>
                )}
              </Box>
            </SectionContent>
          </Box>
        )}
      </Box>
    </ModalDialog>
  );
};

export default ContextInfoPopup;
